import React, { FC, useState } from 'react';
import { titleize } from 'inflection';
import { useForm, SubmitHandler } from 'react-hook-form';
import { faCheckCircle } from '@fortawesome/free-solid-svg-icons/faCheckCircle';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';

import Button, { BUTTON_TYPES, BUTTON_SIZES } from 'buyside/components/presentation/Button/button';
import Link from 'buyside/components/presentation/Link/link';
import LoadingSpinner from 'buyside/components/presentation/LoadingSpinner/loadingSpinner';
import PaladinReviewModal from 'buyside/components/presentation/IdeaShowPage/IdeaStatusBar/PaladinReviewModal/paladinReviewModal';

import {
  qualityGuidelinesUrl,
  userPaladinReviewsUrl,
  adminPaladinPage
} from 'buyside/util/urlHelper';

import { IdeaCoreQuery, Maybe, useUpdatePaladinReviewMutation } from 'interfaces/graphql-typings';

import styles from './ideaStatusPaladinReviewStyles.scss';

const delay = (ms: number): Promise<void> => new Promise(resolve => setTimeout(resolve, ms));

const IdeaStatusPaladinReview: FC<{
  currentUserId: string;
  pendingContestIdeas?: IdeaCoreQuery['idea']['pendingContestIdeas'];
  isAdmin: boolean;
  paladinReviews?: IdeaCoreQuery['idea']['paladinReviews'];
}> = ({ currentUserId, pendingContestIdeas, isAdmin, paladinReviews }) => {
  const [contestOpen, setContestOpen] = useState(false);
  const [formOpen, setFormOpen] = useState(false);
  const [status, setStatus] = useState<Maybe<string>>(null);

  const { register, handleSubmit } = useForm<{
    body: string;
    approvedContestIdeas: string[];
  }>({ defaultValues: { approvedContestIdeas: pendingContestIdeas?.map(ci => ci.id), body: '' } });

  const [handleUpdatePaladinReview, { loading }] = useUpdatePaladinReviewMutation();

  const toggleContest = (): void => setContestOpen(prevState => !prevState);
  const toggleForm = (): void => {
    setFormOpen(!formOpen);
  };

  if (loading)
    return (
      <div className={styles.paladinReview}>
        <div className={styles.loading}>
          <LoadingSpinner size="small" type="secondary" />
        </div>
      </div>
    );

  if (status)
    return (
      <div className={styles[status]}>
        <div className={styles.ellipsis}>
          <FontAwesomeIcon className={styles.faIcon} icon={faCheckCircle} />
          {status === 'approved' && 'Idea Approved. Returning to Paladin Reviews'}
          {status === 'rejected' &&
            'Idea Rejected. Author has been notified. Returning to Paladin Reviews'}
          {status === 'passed' && 'Idea Passed to Next Paladin. Returning to Paladin Reviews'}
        </div>
      </div>
    );

  const currentPaladinReview = paladinReviews?.at(-1);

  const handleUpdate = ({
    approvedContestIdeas,
    body,
    status
  }: {
    approvedContestIdeas?: string[];
    body?: string;
    status: string;
  }): void => {
    if (currentPaladinReview) {
      handleUpdatePaladinReview({
        variables: {
          paladinReviewId: currentPaladinReview?.id,
          approvedContestIdeas,
          description: body,
          status
        }
      })
        .then(({ data }) => {
          const paladinReview = data?.updatePaladinReview?.paladinReview;

          if (paladinReview) {
            setStatus(paladinReview.status);
            delay(2000).then(() => (window.location.href = userPaladinReviewsUrl(currentUserId)));
          }
        })
        .catch(error => {
          throw error;
        });
    }
  };
  const handleContestUpdate: SubmitHandler<{ approvedContestIdeas: string[] }> = (values): void =>
    handleUpdate({ approvedContestIdeas: values.approvedContestIdeas, status: 'approved' });
  const handleRejectedUpdate: SubmitHandler<{ body: string }> = (values): void =>
    handleUpdate({ status: 'rejected', body: values.body });

  return (
    <div className={styles.paladinReview}>
      <div className={styles.leftColumn}>
        {formOpen || contestOpen ? (
          <>
            {contestOpen && (
              <form onSubmit={handleSubmit(handleContestUpdate)}>
                <p>Select contest(s) for which to approve this idea.</p>
                {pendingContestIdeas?.map(pendingContestIdea => (
                  <label key={pendingContestIdea.id} className={styles.contestList}>
                    <input
                      type="checkbox"
                      {...register('approvedContestIdeas')}
                      value={pendingContestIdea.id}
                    />
                    {pendingContestIdea.contestName}
                  </label>
                ))}
                <div>
                  <Button
                    isLoading={loading}
                    size={BUTTON_SIZES.TINY}
                    text="Approve Idea & Contest Entries"
                    type={BUTTON_TYPES.PRIMARY}
                  />
                  <Button
                    disabled={loading}
                    onClick={toggleContest}
                    size={BUTTON_SIZES.TINY}
                    text="Go Back"
                  />
                </div>
              </form>
            )}
            {formOpen && (
              <form onSubmit={handleSubmit(handleRejectedUpdate)}>
                <p>
                  Let us know a little about why you're rejecting this idea (this information will
                  be shared with the author):
                </p>
                <textarea
                  id="paladin_review_description"
                  className={styles.reviewTextArea}
                  {...register('body')}
                />
                <div>
                  <Button
                    isLoading={loading}
                    size={BUTTON_SIZES.TINY}
                    text="Submit"
                    type={BUTTON_TYPES.PRIMARY}
                  />
                  <Button
                    disabled={loading}
                    onClick={toggleForm}
                    size={BUTTON_SIZES.TINY}
                    text="Cancel"
                  />
                </div>
              </form>
            )}
          </>
        ) : (
          <>
            {(currentPaladinReview?.paladin?.userId === currentUserId &&
              currentPaladinReview?.status === 'pending') ||
            isAdmin ? (
              <div>
                {currentPaladinReview?.paladin?.userId !== currentUserId && (
                  <div>
                    <strong>Paladin Review:</strong> This idea is pending approval, but you are not
                    currently assigned as the reviewer. <br /> If you take action as an admin, the
                    currently assigned reviewer will be marked as having "passed" on the idea.
                  </div>
                )}
                <div className={styles.actionsArea}>
                  {pendingContestIdeas?.length ? (
                    <Button
                      id="review_contest"
                      text="Review Contest Entries & Approve"
                      onClick={toggleContest}
                      size={BUTTON_SIZES.TINY}
                      type={BUTTON_TYPES.MAJOR}
                    />
                  ) : (
                    <Button
                      id="approve_idea"
                      text="Approve Idea"
                      onClick={(): void => handleUpdate({ status: 'approved' })}
                      size={BUTTON_SIZES.TINY}
                      type={BUTTON_TYPES.MAJOR}
                    />
                  )}
                  <Button
                    id="reject_idea"
                    text="Reject Idea"
                    onClick={toggleForm}
                    size={BUTTON_SIZES.TINY}
                    type={BUTTON_TYPES.DANGER}
                  />
                  {!isAdmin && (
                    <Button
                      id="pass_on_idea"
                      text="Pass On Idea"
                      onClick={(): void => handleUpdate({ status: 'passed' })}
                      size={BUTTON_SIZES.TINY}
                    />
                  )}
                </div>
                <div className={styles.noteArea}>
                  <strong>Note:</strong> Be sure to review and adhere to the{' '}
                  <Link to={qualityGuidelinesUrl()}>SumZero Quality Guidelines</Link> when
                  evaluating an idea.
                </div>
              </div>
            ) : (
              <p>
                This idea is pending approval, but you are not currently assigned as the reviewer,
                so you cannot take action.
              </p>
            )}
          </>
        )}
      </div>
      <div className={styles.reviewSections}>
        {isAdmin &&
          paladinReviews?.map((review, idx) => (
            <div key={idx} className={styles.review}>
              <div>
                <strong>Review #{idx + 1}</strong>
              </div>
              <div>
                <strong>Name: </strong>
                {review?.paladin && (
                  <Link to={adminPaladinPage(review.paladin.id)}>{review.paladin.displayName}</Link>
                )}
              </div>
              <div>
                <strong>Status:</strong> {titleize(review?.status || '')}
              </div>
              {review?.description && (
                <PaladinReviewModal description={review.description} buttonText="View Review" />
              )}
            </div>
          ))}
      </div>
    </div>
  );
};

export default IdeaStatusPaladinReview;
